import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Alert from '@material-ui/lab/Alert';
import GeoStatusAlert from 'components/GeoStatusAlert';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { makeStyles, CircularProgress } from '@material-ui/core';
import { useDataProvider, useNotify } from 'react-admin';
import { flatten } from 'lodash';
import { luxon } from 'services/luxon';
import { axiosInstance } from 'services/http';
import { PhaseTypeEnum } from '@vatos-pas/common';
import { ContractorDto, BoardTypeDto } from '@vatos-pas/common';
import {
  getModalConfig,
  shouldShowMondayResource,
  BUMP_PHASES,
} from '../../constants/phaseConfigurations';
import {
  isAffectedPhase,
  getPreviousPhaseContractor,
} from '../../utils/phaseRelationships';
import { getRequestResourceLabels } from '../../utils/requestResourceLabels';
import { getMaxCompletionDate } from '../../utils/getMaxCompletionDate';
import { maskDate } from '../../utils/maskDate';
import { useContractorCapacityStatus } from 'hooks/useContractorCapacityStatus';
import { ContractorCapacityLabel } from 'components/ContractorCapacityLabel';
import { phaseTypeToResourceType } from 'utils/phaseTypeToResourceType';
import { PHASE_CONTRACTOR_LIST_LIMIT } from 'utils/phaseUtils';
import { useWorkflowConfig } from '../../config';
import {
  PhaseOptionsSection,
  paintOptionControls,
} from './PhaseOptionsSection';
import { useTrimOptionSelection } from './useTrimOptionSelection';
import { TrimTwoStandingWarning } from './TrimOptionAlerts';

const SPRAY_READY_HOURS: Record<string, number> = {
  '8am': 8,
  '9am': 9,
  '10am': 10,
  '11am': 11,
  '12pm': 12,
  '1pm': 13,
  '2pm': 14,
};

const useStyles = makeStyles({
  dialog: {
    width: '500px',
    '@media (max-width: 800px)': {
      width: 'auto',
    },
  },
  dateStarted: {
    marginTop: '0px',
  },
});

interface RequestResourceModalProps {
  open: boolean;
  handleClose: () => void;
  phaseType: PhaseTypeEnum;
  selectedJobPhase: { id: string; trimOptionSelected?: boolean | null };
  job: any;
  regionId: string;
  refreshJobPhases: () => void;
  geoCoords?: [number, number] | null;
  geoAccuracy?: number | null;
  geoLoading?: boolean;
  requestLocation?: () => void;
  geoError?: string;
}

const getContractorFilter = (
  phaseType: PhaseTypeEnum,
  regionId: string,
  job: any,
) => {
  const filter: any = {
    active: true,
    suspended: false,
    archived: false,
    'contractorRegions.regionId': regionId,
  };

  switch (phaseType) {
    case PhaseTypeEnum.Garage:
    case PhaseTypeEnum.Hanging:
    case PhaseTypeEnum.Hang:
      filter.hangerAvailable = true;
      break;
    case PhaseTypeEnum.Finishing:
    case PhaseTypeEnum.Finish:
      filter.finisherAvailable = true;
      break;
    case PhaseTypeEnum.Spraying:
      filter.sprayerAvailable = true;
      break;
    case PhaseTypeEnum.Prime:
    case PhaseTypeEnum.InteriorPaint:
    case PhaseTypeEnum.FinalPaint:
    case PhaseTypeEnum.CMBluetape:
    case PhaseTypeEnum.FinalBluetape:
      filter.paintAvailable = true;
      break;
    case PhaseTypeEnum.Trim1:
    case PhaseTypeEnum.Trim2:
      filter.trimAvailable = true;
      break;
    case PhaseTypeEnum.Bump1:
    case PhaseTypeEnum.Bump2:
    case PhaseTypeEnum.Bump3:
    case PhaseTypeEnum.Bump4:
    case PhaseTypeEnum.Bump5:
    case PhaseTypeEnum.DrywallTouchUp1:
    case PhaseTypeEnum.DrywallTouchUp2:
    case PhaseTypeEnum.DrywallTouchUp3:
      filter.bumperAvailable = true;
      break;
  }

  if (
    (phaseType === PhaseTypeEnum.Hanging ||
      phaseType === PhaseTypeEnum.Hang ||
      phaseType === PhaseTypeEnum.Finishing ||
      phaseType === PhaseTypeEnum.Finish) &&
    job.job?.highCeiling
  ) {
    filter.canPerformHighCeilingTasks = true;
  }

  return filter;
};

export const RequestResourceModal: React.FC<RequestResourceModalProps> = ({
  open,
  handleClose,
  phaseType,
  selectedJobPhase,
  job,
  regionId,
  refreshJobPhases,
  geoCoords,
  geoAccuracy,
  geoLoading,
  requestLocation,
  geoError,
}) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const config = useWorkflowConfig();

  const modalConfig = getModalConfig(phaseType);
  const showMondayResource = shouldShowMondayResource();
  const showSprayFields =
    config.timeline.sprayReadinessEnabled &&
    phaseType === PhaseTypeEnum.Spraying;
  const trim = useTrimOptionSelection({
    open,
    phase: {
      id: selectedJobPhase.id,
      phaseType,
      trimOptionSelected: selectedJobPhase.trimOptionSelected,
    },
    job: job?.job,
  });

  const [contractors, setContractors] = useState<ContractorDto[]>([]);
  const [contractorSelected, setContractorSelected] = useState('');
  const [completionDate, setCompletionDate] = useState(new Date());

  const [sprayReadiness, setSprayReadiness] = useState('');
  const [sprayHour, setSprayHour] = useState<number | ''>('');

  const [autoSelectedContractor, setAutoSelectedContractor] =
    useState<ContractorDto | null>(null);
  const [showContractorChangeWarning, setShowContractorChangeWarning] =
    useState(false);

  const [mondayResource, setMondayResource] = useState(false);

  const [boardTypes, setBoardTypes] = useState<BoardTypeDto[]>([]);
  const [firstFloorBoardPurchases, setFirstFloorBoardPurchases] = useState<
    any[]
  >([]);
  const [boardTypeSelected, setBoardTypeSelected] = useState<string>('');
  const [boardQuantity, setBoardQuantity] = useState('');

  const [interiorOptions, setInteriorOptions] = useState(false);
  const [exteriorOptions, setExteriorOptions] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contractorsLoading, setContractorsLoading] = useState(false);

  const resourceType = phaseTypeToResourceType(phaseType);
  const { statusMap, loading: capacityLoading } = useContractorCapacityStatus(
    contractors,
    resourceType,
    open,
  );

  useEffect(() => {
    if (job?.job) {
      setExteriorOptions(job.job.exteriorOptionsFinished || false);
      setInteriorOptions(job.job.interiorOptionsFinished || false);
    }
  }, [job]);

  useEffect(() => {
    if (open && requestLocation) {
      requestLocation();
    }
  }, [open, requestLocation]);

  const isLogModal = modalConfig.modalType === 'log';
  const isBumpoutTimesheetRequest =
    BUMP_PHASES.includes(phaseType) && Boolean(job?.job?.reqBumpoutTimesheet);
  const maxCompletionDate = getMaxCompletionDate(
    new Date(),
    isBumpoutTimesheetRequest,
  );
  const isCompletionDateAfterMax = luxon.isAfterDay(
    luxon.date(completionDate),
    luxon.date(maxCompletionDate),
  );

  const saveOptionsToJob = async () => {
    const updateData: any = {};

    // Finished options are monotonic; stale modal state must not reverse them.
    if (modalConfig.components.includes('options')) {
      if (!job.job.interiorOptionsFinished && interiorOptions) {
        updateData.interiorOptionsFinished = true;
        updateData.interiorOptionsFinishedJobPhaseId = selectedJobPhase.id;
      }

      if (!job.job.exteriorOptionsFinished && exteriorOptions) {
        updateData.exteriorOptionsFinished = true;
        updateData.exteriorOptionsFinishedJobPhaseId = selectedJobPhase.id;
      }
    }

    if (Object.keys(updateData).length > 0) {
      await dataProvider.update('job', {
        id: job.id,
        data: updateData,
        previousData: job,
      });
    }
  };

  useEffect(() => {
    if (!open) return;

    const loadContractors = async () => {
      setContractorsLoading(true);
      try {
        const filter = getContractorFilter(phaseType, regionId, job);
        const result = await dataProvider.getList('contractor', {
          pagination: { page: 1, perPage: PHASE_CONTRACTOR_LIST_LIMIT },
          sort: { field: 'name', order: 'ASC' },
          filter: {
            ...filter,
            $join: [{ field: 'contractorRegions' }],
          },
        });
        setContractors(result.data as ContractorDto[]);

        if (isAffectedPhase(phaseType)) {
          const previousContractor = getPreviousPhaseContractor(job, phaseType);
          if (previousContractor) {
            const availableContractor = (result.data as ContractorDto[]).find(
              contractor => contractor.id === previousContractor.id,
            );
            if (availableContractor) {
              setContractorSelected(previousContractor.id);
              setAutoSelectedContractor(availableContractor);
            }
          }
        }
      } catch (error) {
        console.error('Error loading contractors:', error);
      } finally {
        setContractorsLoading(false);
      }
    };

    loadContractors();
  }, [open, phaseType, regionId, job, dataProvider]);

  useEffect(() => {
    if (!open || phaseType !== PhaseTypeEnum.Garage) return;

    const loadBoardData = async () => {
      try {
        const boardPurchasesResult = await dataProvider.getList(
          'board-purchase',
          {
            filter: { jobId: job.job.id },
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'createdAt', order: 'ASC' },
          },
        );

        const boardPurchases = boardPurchasesResult.data;

        // Garage logging uses the earliest purchase order as its quantity limit.
        const firstBoardPurchase = boardPurchases?.[0];
        const flattenedBoardPurchaseFloors = flatten(
          firstBoardPurchase?.boardPurchaseFloors || [],
        );

        const firstFloorBoardPurchasesData =
          flattenedBoardPurchaseFloors?.filter(
            (boardPurchaseFloor: any) => boardPurchaseFloor.floor === 1,
          );

        setFirstFloorBoardPurchases(firstFloorBoardPurchasesData);

        const firstFloorBoardTypeIds = firstFloorBoardPurchasesData?.map(
          (board: any) => board.boardTypeId,
        );

        const boardTypesResult = await dataProvider.getList('board-type', {
          filter: {
            'id||$in': firstFloorBoardTypeIds?.join(),
          },
          pagination: { page: 1, perPage: 999 },
          sort: { field: 'name', order: 'ASC' },
        });

        if (boardTypesResult?.data?.length) {
          setBoardTypes(boardTypesResult.data as BoardTypeDto[]);
        }
      } catch (error) {
        console.error('Error loading board data:', error);
      }
    };

    loadBoardData();
  }, [open, phaseType, job.id, dataProvider]);

  const maxBoardQuantityValue =
    firstFloorBoardPurchases
      ?.filter(
        (boardPurchaseFloor: any) =>
          boardPurchaseFloor.boardTypeId === boardTypeSelected,
      )
      ?.reduce(
        (acc, boardPurchaseFloor) => acc + boardPurchaseFloor.quantity,
        0,
      ) || 0;

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = event.target;
    const numericValue = Number(inputValue);

    if (inputValue === '0' || inputValue === '') {
      setBoardQuantity('');
      return;
    }

    const isValidNumber =
      !isNaN(numericValue) &&
      numericValue > 0 &&
      numericValue <= maxBoardQuantityValue;

    if (isValidNumber) {
      setBoardQuantity(inputValue);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (isLogModal) {
        if (phaseType === PhaseTypeEnum.Garage) {
          const params: Record<string, unknown> = {
            completionDate: completionDate.toISOString(),
            quantity: parseInt(boardQuantity, 10),
            jobPhaseId: selectedJobPhase.id,
            contractorId: contractorSelected,
            boardTypeId: boardTypeSelected,
          };

          if (geoCoords) {
            params.approvedGeo = {
              type: 'Point',
              coordinates: [geoCoords[0], geoCoords[1]],
            };
            params.approvedGeoAccuracy = geoAccuracy;
          }

          try {
            await axiosInstance.post('job-phase-garage', params);
            notify('Garage logged!');
            refreshJobPhases();
            handleClose();
            return;
          } catch (error: any) {
            if (error.response?.data?.message) {
              notify(
                `Garage Log Error: ${error.response.data.message}`,
                'warning',
              );
            } else {
              notify(`Garage Log Error: ${error.message}`, 'warning');
            }
            return;
          } finally {
            setIsSubmitting(false);
          }
        } else {
          const isBumpPhase = [
            PhaseTypeEnum.Bump1,
            PhaseTypeEnum.Bump2,
            PhaseTypeEnum.Bump3,
            PhaseTypeEnum.Bump4,
            PhaseTypeEnum.Bump5,
            PhaseTypeEnum.DrywallTouchUp1,
            PhaseTypeEnum.DrywallTouchUp2,
            PhaseTypeEnum.DrywallTouchUp3,
            PhaseTypeEnum.CMBluetape,
            PhaseTypeEnum.FinalBluetape,
          ].includes(phaseType);

          if (isBumpPhase) {
            // The bump endpoint expects completion time in `date`.
            const params: Record<string, unknown> = {
              jobPhaseId: selectedJobPhase.id,
              date: completionDate.toISOString(),
              contractorId: contractorSelected,
            };

            if (showMondayResource && mondayResource) {
              params.mondayResource = true;
            }

            if (trim.show) {
              // The backend stores the selection on the phase and finalizes it on approval.
              params.trimOptionSelected = trim.selected;
            }

            if (!job?.job?.reqBumpoutTimesheet && geoCoords) {
              // The bump endpoint accepts logging coordinates, not approval coordinates.
              params.loggedGeo = {
                type: 'Point',
                coordinates: [geoCoords[0], geoCoords[1]],
              };
              params.loggedGeoAccuracy = geoAccuracy;
            }

            try {
              await axiosInstance.post('job-phase-bump', params);

              if (phaseType === PhaseTypeEnum.CMBluetape) {
                await saveOptionsToJob();
              }

              notify('Bump Logged!');
              refreshJobPhases();
              handleClose();
              return;
            } catch (error: any) {
              if (error.response?.data?.message) {
                notify(
                  `Bump Log Error: ${error.response.data.message}`,
                  'warning',
                );
              } else {
                notify(`Bump Log Error: ${error.message}`, 'warning');
              }
              return;
            } finally {
              setIsSubmitting(false);
            }
          }
        }
      } else {
        const payload: any = {
          requested: true,
        };

        if (contractorSelected) {
          if (
            isAffectedPhase(phaseType) &&
            autoSelectedContractor &&
            contractorSelected === autoSelectedContractor.id
          ) {
            // Reusing the previous contractor is a direct assignment.
            payload.contractorId = contractorSelected;
          } else {
            // Other selections require approval before assignment.
            payload.suggestedContractorId = contractorSelected;
          }
        }

        if (showMondayResource && mondayResource) {
          // Saturday requests need a two-day delay to target Monday.
          payload.requestDelayDays = 2;
        } else {
          payload.requestDelayDays = 0;
        }

        if (modalConfig.components.includes('options')) {
          // Finished options are monotonic; stale modal state must not reverse them.
          payload.interiorOptions = job.job.interiorOptionsFinished
            ? true
            : interiorOptions;
          payload.exteriorOptions = job.job.exteriorOptionsFinished
            ? true
            : exteriorOptions;
        }

        if (showSprayFields) {
          payload.readinessStatus = sprayReadiness;
          if (sprayHour !== '') {
            payload.readyHour = sprayHour;
          }
        }

        if (trim.show) {
          // The backend stores the selection on the phase and finalizes it on approval.
          payload.trimOptionSelected = trim.selected;
        }

        await dataProvider.update('job-phase', {
          id: selectedJobPhase.id,
          data: payload,
          previousData: selectedJobPhase,
        });

        await saveOptionsToJob();

        notify(
          trim.show
            ? 'Resource Request sent successfully.'
            : 'Resource Requested!',
        );
      }

      refreshJobPhases();
      handleClose();
    } catch (error) {
      console.error('Error submitting request:', error);
      notify('Unable to send request. Please try again later.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    if (modalConfig.modalType === 'log') {
      if (!contractorSelected) return false;

      if (phaseType === PhaseTypeEnum.Garage) {
        return (
          boardTypeSelected && boardQuantity && parseInt(boardQuantity) > 0
        );
      }

      if (
        phaseType === PhaseTypeEnum.CMBluetape &&
        modalConfig.components.includes('options')
      ) {
        const hasUnfinishedInterior =
          job?.job?.hasInteriorOptions && !job?.job?.interiorOptionsFinished;
        const hasUnfinishedExterior =
          job?.job?.hasExteriorOptions && !job?.job?.exteriorOptionsFinished;

        if (hasUnfinishedInterior && !interiorOptions) return false;
        if (hasUnfinishedExterior && !exteriorOptions) return false;
      }

      return true;
    }

    if (showSprayFields) {
      if (!sprayReadiness) return false;
      if (sprayReadiness === 'ReadyLater' && sprayHour === '') return false;
    }

    return true;
  };

  const { title: modalTitle, submitLabel } = getRequestResourceLabels({
    phaseType,
    isLogModal,
    isBumpoutTimesheetRequest,
  });

  const paintOptions = paintOptionControls(job?.job, {
    exterior: {
      checked: exteriorOptions,
      onChange: setExteriorOptions,
      disabled: job?.job?.exteriorOptionsFinished,
    },
    interior: {
      checked: interiorOptions,
      onChange: setInteriorOptions,
      disabled: job?.job?.interiorOptionsFinished,
    },
  });

  const requestOrLogOptions = [
    ...(modalConfig.components.includes('options') ? paintOptions : []),
    ...(trim.control ? [trim.control] : []),
  ];

  const hasUnfinishedPaintOptions = Boolean(
    (job?.job?.hasInteriorOptions && !job?.job?.interiorOptionsFinished) ||
      (job?.job?.hasExteriorOptions && !job?.job?.exteriorOptionsFinished),
  );

  let requestOptionsWarning: React.ReactNode;
  if (trim.isTrimTwo) {
    requestOptionsWarning = <TrimTwoStandingWarning />;
  } else if (
    modalConfig.warningMessage &&
    phaseType === PhaseTypeEnum.CMBluetape &&
    hasUnfinishedPaintOptions
  ) {
    requestOptionsWarning = (
      <Alert severity="warning" style={{ marginTop: 16 }}>
        {modalConfig.warningMessage}
      </Alert>
    );
  }

  const confirmationOptionsWarning =
    modalConfig.warningMessage &&
    phaseType === PhaseTypeEnum.FinalBluetape &&
    hasUnfinishedPaintOptions ? (
      <Alert severity="warning" style={{ marginTop: 16 }}>
        {modalConfig.warningMessage}
      </Alert>
    ) : undefined;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>{modalTitle}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column">
          {modalConfig.components.includes('contractor') && (
            <>
              <Box display="flex">
                <Typography>Select a Contractor</Typography>
              </Box>
              <Box>
                <Box display="flex">
                  <Select
                    onChange={(event: any) => {
                      const newContractorId = event.target.value;
                      setContractorSelected(newContractorId);

                      if (
                        autoSelectedContractor &&
                        newContractorId !== autoSelectedContractor.id
                      ) {
                        setShowContractorChangeWarning(true);
                      } else {
                        setShowContractorChangeWarning(false);
                      }
                    }}
                    labelId="custom-reference-contractor"
                    id="reference-contractor"
                    fullWidth
                    value={contractorSelected}
                    renderValue={value => {
                      const c = contractors.find(x => x.id === value);
                      if (!c) return 'Select a contractor';
                      return (
                        <ContractorCapacityLabel
                          name={c.name}
                          isOverCapacity={statusMap[c.id]?.isOverCapacity}
                          variant="selected"
                        />
                      );
                    }}
                  >
                    <MenuItem
                      key="Select"
                      value=""
                      data-item=""
                      data-name="select"
                    >
                      Select a contractor
                    </MenuItem>
                    {contractorsLoading || capacityLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={16} />
                      </MenuItem>
                    ) : (
                      contractors.map((item: ContractorDto) => (
                        <MenuItem
                          key={item.id}
                          value={item.id}
                          data-item={item.id}
                          data-name={item.name}
                        >
                          <ContractorCapacityLabel
                            name={item.name}
                            isOverCapacity={statusMap[item.id]?.isOverCapacity}
                          />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </Box>
              </Box>

              {showContractorChangeWarning && (
                <Box mt={2} textAlign="center">
                  <Typography variant="body2" color="error">
                    Changing the assigned Contractor will require approval
                  </Typography>
                </Box>
              )}
            </>
          )}

          {showMondayResource && modalConfig.mondayResource && (
            <Box mt={2}>
              <Typography>
                This request will be for a Saturday resource, if you need it for
                Monday, please check below:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={mondayResource}
                    onChange={e => setMondayResource(e.target.checked)}
                    color="primary"
                  />
                }
                label="Monday Resource"
              />
            </Box>
          )}

          {showSprayFields && (
            <>
              <Box display="flex" mt={2}>
                <Typography>Spray Readiness</Typography>
              </Box>
              <Box display="flex" flexDirection="column">
                <Select
                  onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                    const value = event.target.value as string;
                    setSprayReadiness(value);
                    setSprayHour('');
                  }}
                  labelId="spray-readiness-label"
                  id="reference-spray-readiness"
                  fullWidth
                  value={sprayReadiness}
                >
                  <MenuItem key="ReadyLater" value="ReadyLater">
                    Ready Later (Select Time)
                  </MenuItem>
                  <MenuItem key="ReadyNow" value="ReadyNow">
                    Ready Now
                  </MenuItem>
                  <MenuItem key="Done" value="Done">
                    Done
                  </MenuItem>
                </Select>
              </Box>
              <Box display="flex" mt={2}>
                <Typography>Spray Request Time</Typography>
              </Box>
              <Box display="flex" flexDirection="column">
                <Select
                  onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                    setSprayHour(event.target.value as number);
                  }}
                  labelId="spray-hour-label"
                  id="reference-spray-request-time"
                  fullWidth
                  disabled={
                    sprayReadiness === 'ReadyNow' || sprayReadiness === 'Done'
                  }
                  value={sprayHour}
                >
                  <MenuItem key="Select" value="">
                    Select a Time
                  </MenuItem>
                  {Object.keys(SPRAY_READY_HOURS).map(hour => (
                    <MenuItem key={hour} value={SPRAY_READY_HOURS[hour]}>
                      {hour}
                    </MenuItem>
                  ))}
                </Select>
                {(sprayReadiness === 'ReadyNow' ||
                  sprayReadiness === 'Done') && (
                  <Box mt={1}>
                    <Alert severity="warning">
                      Spray request time cannot be selected when &apos;Ready
                      Now&apos; or &apos;Done&apos; is selected.
                    </Alert>
                  </Box>
                )}
              </Box>
            </>
          )}

          {modalConfig.components.includes('completionDate') && (
            <Box display="flex" flexDirection="column" mt={2}>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="Completion Date"
                fullWidth
                maxDate={maxCompletionDate}
                value={completionDate}
                onChange={selected => {
                  if (!selected) return;
                  setCompletionDate(
                    selected instanceof Date ? selected : selected.toJSDate(),
                  );
                }}
                KeyboardButtonProps={{ 'aria-label': 'change date' }}
              />
            </Box>
          )}

          {modalConfig.components.includes('boardType') && (
            <>
              <Box display="flex" mt={2}>
                <Typography>Board Type</Typography>
              </Box>
              <Box display="flex">
                <FormControl fullWidth>
                  <Select
                    onChange={(event: any) => {
                      setBoardQuantity('');
                      setBoardTypeSelected(event.target.value);
                    }}
                    labelId="custom-reference-board-type"
                    id="reference-board-type"
                    fullWidth
                    inputProps={{
                      'aria-label':
                        'The available Board Types are those listed for Floor 1 in the Purchase Order',
                    }}
                    value={boardTypeSelected}
                  >
                    {boardTypes.map(item => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                        data-item={item.id}
                        data-name={item.shortName}
                      >
                        {item.shortName}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    The available Board Types are those listed for Floor 1 in
                    the Purchase Order
                  </FormHelperText>
                </FormControl>
              </Box>
            </>
          )}

          {modalConfig.components.includes('boardQuantity') && (
            <>
              <Box display="flex" mt={2}>
                <Typography>Board Quantity</Typography>
              </Box>
              <Box display="flex">
                <TextField
                  fullWidth
                  type="number"
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: maxBoardQuantityValue,
                    },
                  }}
                  helperText="The Board Quantity can't exceed the amount in the Purchase Order"
                  disabled={!boardTypeSelected}
                  value={boardQuantity}
                  onChange={handleQuantityChange}
                />
              </Box>
            </>
          )}

          {requestOrLogOptions.length > 0 && (
            <PhaseOptionsSection
              prompt={
                trim.show
                  ? 'Select whether the contractor should complete an option during this phase.'
                  : 'Select whether the contractor should paint interior and/or exterior options during this phase.'
              }
              options={requestOrLogOptions}
              supplementalContent={requestOptionsWarning}
            />
          )}

          {modalConfig.components.includes('optionsConfirmation') &&
            (job?.job?.hasExteriorOptions || job?.job?.hasInteriorOptions) && (
              <PhaseOptionsSection
                prompt="Confirm whether the contractor painted the selected interior and/or exterior options during this phase."
                options={paintOptions}
                supplementalContent={confirmationOptionsWarning}
              />
            )}

          {isCompletionDateAfterMax &&
            modalConfig.components.includes('completionDate') && (
              <Box mt={3}>
                <Alert severity="warning">
                  {isBumpoutTimesheetRequest
                    ? `Completion date cannot be after ${maskDate(
                        maxCompletionDate,
                      )}`
                    : 'Completion date cannot be in the future'}
                </Alert>
              </Box>
            )}
        </Box>

        <GeoStatusAlert
          loading={geoLoading ?? false}
          error={geoError}
          coords={geoCoords}
        />

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button
            onClick={() => {
              setContractorSelected('');
              setSprayReadiness('');
              setSprayHour('');
              handleClose();
            }}
            color="primary"
          >
            {isLogModal ? 'Cancel' : 'Cancel request'}
          </Button>

          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={
              !isFormValid() || isCompletionDateAfterMax || isSubmitting
            }
          >
            {submitLabel}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RequestResourceModal;
